import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import {
  isFedexCarrier,
  isUpsCarrier,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { OutboundRate } from "@redotech/redo-model/outbound-labels/outbound-labels";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Card } from "@redotech/redo-web/card";
import { getCarrierServiceLabel } from "@redotech/redo-web/carrier-service-labels";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../team";

export const LookupRateDetailsCard = memo(function LookupRateDetailsCard() {
  const client = useRequiredContext(RedoAdminRpcClientContext);
  const team = useRequiredContext(TeamContext);

  const [groupId, setGroupId] = useState<string>("");

  const [rateDetailsLoad, triggerRateDetailsLoad] = useTriggerLoad(async () => {
    const { carrier, service } = selectedRate ?? {};

    const result = await client.getShipmentRateDetails({
      fulfillmentGroupId: groupId,
    });

    let matchingRate = result.rateDetails.find(
      (rate: OutboundRate) =>
        rate.carrier === carrier && rate.service === service,
    );

    if (!matchingRate) {
      matchingRate = result.rateDetails[0];
    }

    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    setSelectedRate(matchingRate);
    return result;
  });

  const [selectedRate, setSelectedRate] = useState<OutboundRate | null>(null);

  const [ghostAccountsVisible, setGhostAccountsVisible] = useState(false);

  const [ghostAccountsLoad, triggerGhostAccountsLoad] = useTriggerLoad(
    async () => {
      const result = await client.getUpsGhostAccounts({
        teamId: team._id.toString(),
      });
      return result.locations;
    },
  );

  const options: RedoListItem<OutboundRate>[] = useMemo(() => {
    return (
      rateDetailsLoad.value?.rateDetails.map((rate) => ({
        value: rate,
        type: "text",
        id: rate.id,
        text: getCarrierServiceLabel(rate.carrier, rate.service),
      })) ?? []
    );
  }, [rateDetailsLoad.value]);

  const ghostAccountIds = rateDetailsLoad.value?.ghostAccountIds ?? [];

  const handleToggleGhostAccounts = () => {
    if (!ghostAccountsVisible) {
      triggerGhostAccountsLoad();
    }
    setGhostAccountsVisible((prev) => !prev);
  };

  return (
    <Card
      subtitle="This will only load the details of the rates we already have. To see changes from the rate table reflected, you will need to reload the rates from the OMS. If the shipment has been purchased, you will see the details of the purchased rate here as well"
      title="Lookup Rate Details"
    >
      <Flex dir="row">
        <RedoTextInput
          fullWidth
          placeholder="67bdcae4433b1abda5387360"
          setValue={setGroupId}
          value={groupId}
        />
        <RedoButton
          disabled={groupId.length === 0}
          hierarchy="primary"
          onClick={() => {
            triggerRateDetailsLoad();
          }}
          pending={rateDetailsLoad.pending}
          text="Load rates"
          theme="success"
        />
      </Flex>
      {ghostAccountIds.length > 0 && (
        <Flex dir="column">
          <Text fontSize="sm" fontWeight="medium">
            Ghost account IDs
          </Text>
          <ul>
            {ghostAccountIds.map((id) => (
              <li key={id}>{id}</li>
            ))}
          </ul>
        </Flex>
      )}
      {rateDetailsLoad.value && (
        <Flex dir="column">
          <RedoSingleSelectDropdownInput<OutboundRate>
            disabled={!rateDetailsLoad.value}
            options={options}
            optionSelected={({ item }) => {
              setSelectedRate(item.value);
            }}
            placeholder="Select rate"
            selectedItem={options.find((o) => o.id === selectedRate?.id)}
          />
          {selectedRate && (
            <ul>
              <li>{`Margin: $${(
                Number(selectedRate?.rate) - Number(selectedRate?.provider_cost)
              ).toFixed(2)}`}</li>
              <li>{`Rev share margin: ${selectedRate?.rev_share_margin}`}</li>
              <li>{`Merchant cost: $${selectedRate?.rate}`}</li>
              <li>{`Redo cost: $${selectedRate?.provider_cost}`}</li>
              {isUpsCarrier(selectedRate.carrier) && (
                <li>{`UPS zone: ${selectedRate?.ups_zone}`}</li>
              )}
              {isFedexCarrier(selectedRate.carrier) && (
                <li>{`Fedex zone: ${selectedRate?.fedex_zone}`}</li>
              )}
              <li>{`USPS zone: ${selectedRate?.usps_zone}`}</li>
              <li>{`Carrier account: ${selectedRate?.carrier_account_id}`}</li>
              <li>{`Provider: ${selectedRate?.provider}`}</li>
            </ul>
          )}
        </Flex>
      )}
      <Flex dir="column" gap="md">
        <RedoButton
          hierarchy="secondary"
          onClick={handleToggleGhostAccounts}
          pending={ghostAccountsLoad.pending}
          text={
            ghostAccountsVisible
              ? "Hide UPS ghost accounts"
              : "View UPS ghost accounts"
          }
        />
        {ghostAccountsVisible && (
          <Flex dir="column" gap="sm">
            {ghostAccountsLoad.error && (
              <Text fontSize="sm" textColor="error">
                Error loading ghost accounts: {ghostAccountsLoad.error.message}
              </Text>
            )}
            {ghostAccountsLoad.value && (
              <>
                {ghostAccountsLoad.value.length === 0 ? (
                  <Text fontSize="sm" textColor="secondary">
                    No UPS ghost accounts found for this merchant.
                  </Text>
                ) : (
                  ghostAccountsLoad.value.map((loc) => (
                    <Flex
                      borderColor="secondary"
                      borderStyle="solid"
                      borderWidth="1px"
                      dir="column"
                      gap="xs"
                      key={loc}
                      p="md"
                      radius="md"
                    >
                      <Text fontSize="sm">{`Account: ${loc}`}</Text>
                    </Flex>
                  ))
                )}
              </>
            )}
          </Flex>
        )}
      </Flex>
    </Card>
  );
});
